/*
 **	Unit Preference Context - Manage pressure display units
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React, { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import type { PressureUnit } from '../utils/unitConversion'

interface UnitPreferenceContextType {
  pressureUnit: PressureUnit
  setPressureUnit: (unit: PressureUnit) => void
}

const UnitPreferenceContext = createContext<UnitPreferenceContextType | undefined>(undefined)

const STORAGE_KEY = 'omniiq_pressure_unit'

interface UnitPreferenceProviderProps {
  children: ReactNode
}

export const UnitPreferenceProvider: React.FC<UnitPreferenceProviderProps> = ({ children }) => {
  const [pressureUnit, setPressureUnitState] = useState<PressureUnit>(() => {
    // Load from localStorage on initialization
    const stored = localStorage.getItem(STORAGE_KEY)
    return (stored === 'Pa' || stored === 'inWC') ? stored : 'inWC'
  })

  const setPressureUnit = (unit: PressureUnit) => {
    setPressureUnitState(unit)
    localStorage.setItem(STORAGE_KEY, unit)
  }

  // Sync with localStorage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'Pa' || e.newValue === 'inWC')) {
        setPressureUnitState(e.newValue)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  return (
    <UnitPreferenceContext.Provider value={{ pressureUnit, setPressureUnit }}>
      {children}
    </UnitPreferenceContext.Provider>
  )
}

export const useUnitPreference = (): UnitPreferenceContextType => {
  const context = useContext(UnitPreferenceContext)
  if (context === undefined) {
    throw new Error('useUnitPreference must be used within a UnitPreferenceProvider')
  }
  return context
}
